import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Cpu as CpuIcon,
  FileText as FileTextIcon,
  Radio as RadioIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  collectAdditionalProperties,
  useGetPlantSafetyPointDetailQuery,
  type PlantSafetyPoint
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import {
  plantFunctionLogicDiagramHref,
  plantLogicSolverHref,
  plantProtectiveFunctionHref,
  plantSafetyIndexHref
} from '../../lib/plant-links'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { SectionHeader } from './entity/SectionHeader'
import { PlantHref } from './PlantHref'

/**
 * `/plant/safety/points/:pointId` — Safety Manager point detail.
 *
 * Shows identity + I/O location, SRS functions that `IMPLEMENTED_BY`
 * this point, and Equipment reached via `MAPS_TO_SAFETY_POINT`.
 */
const SafetyPointDetailPage = (): React.ReactElement => {
  const { pointId = '' } = useParams<{ pointId: string }>()
  const decodedPointId = decodeRouteParam(pointId)
  const { data, isLoading, isError } = useGetPlantSafetyPointDetailQuery(decodedPointId, {
    skip: !decodedPointId
  })

  const point = data?.point
  const functions = React.useMemo(() => data?.functions ?? [], [data?.functions])
  const equipment = React.useMemo(() => data?.equipment ?? [], [data?.equipment])
  const additionalProperties = React.useMemo(
    () =>
      point
        ? collectAdditionalProperties('SafetyPoint', point as unknown as Record<string, unknown>)
        : [],
    [point]
  )

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantSafetyIndexHref()} className="hover:text-ink hover:underline">
          Safety
        </Link>
        {data?.solver && (
          <>
            <span className="mx-1.5 text-ink-4">/</span>
            <Link
              to={plantLogicSolverHref(data.solver.id)}
              className="hover:text-ink hover:underline"
            >
              {data.solver.name}
            </Link>
          </>
        )}
        {data?.diagram && (
          <>
            <span className="mx-1.5 text-ink-4">/</span>
            <Link
              to={plantFunctionLogicDiagramHref(data.diagram.id)}
              className="hover:text-ink hover:underline"
            >
              {data.diagram.name}
            </Link>
          </>
        )}
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{point?.name ?? decodedPointId}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this safety point"
          hint="The point may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading safety point…" hint="" muted />
      ) : (
        <>
          <header className="flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <RadioIcon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
                  {point?.name ?? decodedPointId}
                </h1>
                {point?.point_type && <Badge variant="secondary">{point.point_type}</Badge>}
                {point?.safety_related && <Badge variant="outline">Safety related</Badge>}
                {point?.is_internal && <Badge variant="secondary">Internal</Badge>}
                {point?.sil && <Badge variant="outline">SIL {point.sil}</Badge>}
              </div>
              <p className="m-0 mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-ink-3">
                {data.solver && (
                  <>
                    <CpuIcon size={14} aria-hidden />
                    <Link
                      to={plantLogicSolverHref(data.solver.id)}
                      className="hover:text-ink hover:underline"
                    >
                      {data.solver.name}
                    </Link>
                  </>
                )}
                {data.diagram && (
                  <>
                    <span className="text-ink-4">·</span>
                    <FileTextIcon size={14} aria-hidden />
                    <Link
                      to={plantFunctionLogicDiagramHref(data.diagram.id)}
                      className="hover:text-ink hover:underline"
                    >
                      {data.diagram.name}
                    </Link>
                  </>
                )}
              </p>
              {point?.description && (
                <p className="m-0 mt-3 text-sm text-ink-2">{point.description}</p>
              )}
            </div>
          </header>

          <PointSpecs point={point} />

          <section>
            <SectionHeader
              label="Implements"
              subtitle={`${functions.length} ${functions.length === 1 ? 'function' : 'functions'}`}
            />
            {functions.length === 0 ? (
              <EmptyCard
                title="No linked protective functions"
                hint="SRS SIFs join this point when the ISA tag core matches."
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {functions.map((fn) => (
                  <Link
                    key={fn.id}
                    to={plantProtectiveFunctionHref(fn.id)}
                    className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-soft px-2 py-0.5 text-11 font-medium text-brand no-underline hover:bg-brand-soft/70"
                  >
                    <ShieldIcon size={11} aria-hidden />
                    {fn.name}
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section>
            <SectionHeader
              label="Mapped equipment"
              subtitle={`${equipment.length} ${equipment.length === 1 ? 'asset' : 'assets'}`}
            />
            {equipment.length === 0 ? (
              <EmptyCard
                title="No mapped equipment"
                hint="Run the Safety Manager ↔ SRS mapping job to link matching SRS equipment to this point."
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {equipment.map((item) => (
                  <PlantHref
                    key={item.id}
                    id={item.id}
                    label="Equipment"
                    node={item}
                    className="inline-flex items-center rounded-md border bg-card px-2.5 py-1 text-sm font-medium text-ink no-underline shadow-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    {item.name}
                  </PlantHref>
                ))}
              </div>
            )}
          </section>

          {point && (
            <EntityDetailsInspector entity={point} additionalProperties={additionalProperties} />
          )}
        </>
      )}
    </div>
  )
}

const SPEC_FIELDS: Array<[string, keyof PlantSafetyPoint]> = [
  ['Tag number', 'tag_number'],
  ['Location', 'location'],
  ['Unit', 'unit'],
  ['Subunit', 'subunit'],
  ['Chassis', 'chassis_id'],
  ['Slot', 'slot_number'],
  ['Channel', 'channel_number'],
  ['Address', 'application_address'],
  ['Block', 'block_name'],
  ['Data type', 'data_type'],
  ['Signal', 'signal_type'],
  ['Units', 'engineering_units']
]

function PointSpecs({ point }: { point: PlantSafetyPoint | undefined }): React.ReactElement | null {
  if (!point) return null
  const specs: Array<[string, string]> = []
  for (const [label, field] of SPEC_FIELDS) {
    const value = point[field]
    if (typeof value === 'string' && value.trim()) specs.push([label, value])
  }
  if (specs.length === 0) return null
  return (
    <section>
      <SectionHeader label="I/O" />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(11.25rem,1fr))]">
          {specs.map(([k, v]) => (
            <div key={k}>
              <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
                {k}
              </div>
              <div className="mt-0.5 text-13 font-medium text-ink">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function EmptyCard({ title, hint }: { title: string; hint: string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      <Empty title={title} hint={hint} icon={RadioIcon} />
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default SafetyPointDetailPage
